import type {FC} from 'react';
import {useCallback, useEffect, useState} from 'react';
import {styled} from 'styled-components';

import {notifyError} from '@repo/shared-web/lib/notification';

import {apiCall} from '@repo/website-frontend/lib/api';

export const NewsgroupToggle: FC = () => {
  const [newsgroupEnabled, setNewsgroupEnabled] = useState<boolean>();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    apiCall('POST /get-nzb-check-settings', {})
      .then(({newsgroupEnabled: enabled}) => {
        if (active) {
          setNewsgroupEnabled(enabled);
        }
      })
      .catch(error => {
        if (active) {
          notifyError(error);
        }
      });
    return () => {
      active = false;
    };
  }, []);

  const handleToggle = useCallback(async (enabled: boolean) => {
    setSaving(true);
    try {
      const {newsgroupEnabled: savedValue} = await apiCall('POST /set-nzb-check-settings', {
        newsgroupEnabled: enabled,
      });
      setNewsgroupEnabled(savedValue);
    } catch (error) {
      notifyError(error);
    } finally {
      setSaving(false);
    }
  }, []);

  const disabled = newsgroupEnabled === undefined || saving;

  return (
    <Control $disabled={disabled}>
      <span>Newsgroup</span>
      <SwitchInput
        type="checkbox"
        role="switch"
        aria-label="Allow automatic NZB checks to use the newsgroup account"
        aria-checked={newsgroupEnabled ?? false}
        checked={newsgroupEnabled ?? false}
        disabled={disabled}
        onChange={event => {
          void handleToggle(event.currentTarget.checked);
        }}
      />
      <SwitchTrack aria-hidden="true">
        <SwitchThumb />
      </SwitchTrack>
      <SwitchState>{newsgroupEnabled ? 'On' : newsgroupEnabled === false ? 'Off' : '…'}</SwitchState>
    </Control>
  );
};
NewsgroupToggle.displayName = 'NewsgroupToggle';

const Control = styled.label<{$disabled: boolean}>`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  color: #ffffffcc;
  cursor: ${p => (p.$disabled ? 'default' : 'pointer')};
  opacity: ${p => (p.$disabled ? 0.6 : 1)};
  font-size: 14px;
`;

const SwitchInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;

  &:focus-visible + span {
    outline: 2px solid #ffffff;
    outline-offset: 3px;
  }

  &:checked + span {
    background: #36a878;
  }

  &:checked + span > span {
    transform: translateX(14px);
  }
`;

const SwitchTrack = styled.span`
  display: inline-flex;
  align-items: center;
  width: 32px;
  height: 18px;
  padding: 2px;
  border-radius: 999px;
  background: #666d7a;
  transition: background 120ms ease;
`;

const SwitchThumb = styled.span`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #ffffff;
  transition: transform 120ms ease;
`;

const SwitchState = styled.span`
  min-width: 22px;
`;
